/**
 * Browser page translation (Chrome/Google Translate, Edge) replaces React-owned text nodes
 * with <font> elements. React later tries to remove or insert relative to the original nodes,
 * the DOM throws NotFoundError, and the whole app unmounts (white screen).
 *
 * Make removeChild/insertBefore tolerate nodes that are no longer where React expects them.
 * See https://github.com/facebook/react/issues/11538#issuecomment-417504600
 */
const patchDomForTranslate = () => {
  if (typeof Node !== "function" || !Node.prototype) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      // Already detached (e.g. replaced by a translation <font>): nothing to remove
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // Reference node was moved away by the translator: append instead of throwing
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
};

export default patchDomForTranslate;
